import axios from './axiosInstance';
import axiosLib from 'axios';
import type {
  APICalendar,
  APICalendarComponent,
  CalendarComponentWriteBody,
  LinkedFormResponseSummary,
  CalendarComponentComponentId,
  CalendarComponentRecurrenceId,
} from '../../types';

// ── shared param/response shapes ───────────────────────────────────────────
interface SportArea {
  sport: string;
  areaId: string;
}

export interface SearchComponentsPageInfo {
  hasMoreFuture: boolean;
  hasMorePast: boolean;
  futureCursor?: string;
  pastCursor?: string;
}

export interface SearchComponentsResponse {
  results: APICalendarComponent[];
  pageInfo: SearchComponentsPageInfo;
  totalMasterMatches: number;
}

export interface LinkFormResponseOperation {
  action: 'link' | 'unlink';
  formResponseId: string;
  recurrenceId?: CalendarComponentRecurrenceId;
  updateType?: 'single' | 'thisAndFuture';
}

export interface LinkFormResponsesResult {
  formResponseId: string;
  action: string;
  success: boolean;
  componentLink?: string;
  error?: string;
}

export interface LinkFormResponsesResponse {
  success: boolean;
  results: LinkFormResponsesResult[];
  component: APICalendarComponent;
}

const cancelTokenFor = (abortSignal?: AbortSignal) => {
  const source = axiosLib.CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => source.cancel('Operation canceled by the user.'));
  }
  return source.token;
};

// ── calendars ───────────────────────────────────────────────────────────────
interface GetCalendarsParams extends SportArea {
  abortSignal?: AbortSignal;
}
const getCalendars = ({ sport, areaId, abortSignal }: GetCalendarsParams): Promise<APICalendar[]> =>
  axios
    .get(`/${sport}/areas/${areaId}/calendars`, { cancelToken: cancelTokenFor(abortSignal) })
    .then((r) => (r.data && r.data.calendars) || [])
    .catch((e) => {
      console.log(e);
      throw e;
    });

interface CreateCalendarParams extends SportArea {
  name: string;
  time_zone: string;
  color: string;
  alias?: string;
  description?: string;
  sports?: string[];
}
// PUT = create (Ullr convention).
const createCalendar = ({
  sport,
  areaId,
  ...body
}: CreateCalendarParams): Promise<APICalendar> =>
  axios
    .put(`/${sport}/areas/${areaId}/calendars`, body)
    .then((r) => r.data.calendar)
    .catch((e) => {
      console.log(e);
      throw e;
    });

interface UpdateCalendarParams extends SportArea {
  calendarId: string;
  name?: string;
  description?: string;
  time_zone?: string;
  color?: string;
}
// POST = update (Ullr convention).
const updateCalendar = ({
  sport,
  areaId,
  calendarId,
  ...body
}: UpdateCalendarParams): Promise<APICalendar> =>
  axios
    .post(`/${sport}/areas/${areaId}/calendars/${calendarId}`, body)
    .then((r) => r.data.calendar)
    .catch((e) => {
      console.log(e);
      throw e;
    });

interface DeleteCalendarParams extends SportArea {
  calendarId: string;
}
const deleteCalendar = ({
  sport,
  areaId,
  calendarId,
}: DeleteCalendarParams): Promise<{ success: boolean }> =>
  axios
    .delete(`/${sport}/areas/${areaId}/calendars/${calendarId}`)
    .then((r) => r.data)
    .catch((e) => {
      console.log(e);
      throw e;
    });

// ── components (read) ─────────────────────────────────────────────────────
interface GetComponentsParams extends SportArea {
  timeMin: string; // ISO (required)
  timeMax: string; // ISO (required)
  calendarIds?: string[];
  expand?: boolean;
  componentType?: string[];
  status?: string[];
  attendee?: string;
  tags?: string[];
  priority?: string[];
  includeCancelled?: boolean;
  abortSignal?: AbortSignal;
}
/** Primary list fetch — components across (optionally several) calendars. */
const getComponents = ({
  sport,
  areaId,
  timeMin,
  timeMax,
  calendarIds,
  expand = true,
  componentType,
  status,
  attendee,
  tags,
  priority,
  includeCancelled,
  abortSignal,
}: GetComponentsParams): Promise<APICalendarComponent[]> => {
  const params: Record<string, string> = { timeMin, timeMax, expand: String(expand) };
  if (calendarIds && calendarIds.length) {
    params.calendarIds = calendarIds.join(',');
  }
  if (componentType && componentType.length) {
    params.componentType = componentType.join(',');
  }
  if (status && status.length) {
    params.status = status.join(',');
  }
  if (attendee) {
    params.attendee = attendee;
  }
  if (tags && tags.length) {
    params.tags = tags.join(',');
  }
  if (priority && priority.length) {
    params.priority = priority.join(',');
  }
  if (includeCancelled) {
    params.includeCancelled = 'true';
  }
  return axios
    .get(`/${sport}/areas/${areaId}/calendarcomponents`, {
      params,
      cancelToken: cancelTokenFor(abortSignal),
    })
    .then((r) => (r.data && r.data.components) || [])
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

interface GetComponentParams extends SportArea {
  componentId: CalendarComponentComponentId;
  calendarId?: string;
  recurrenceId?: CalendarComponentRecurrenceId; // NOTE: GET uses `recurrenceId` (camelCase)
}
/** Single component. With calendarId → includes pendingAttendees. */
const getComponent = ({
  sport,
  areaId,
  componentId,
  calendarId,
  recurrenceId,
}: GetComponentParams): Promise<APICalendarComponent> => {
  const url = calendarId
    ? `/${sport}/areas/${areaId}/calendars/${calendarId}/components/${componentId}`
    : `/${sport}/areas/${areaId}/calendarcomponent/${componentId}`;
  const params: Record<string, string> = {};
  if (recurrenceId) {
    params.recurrenceId = recurrenceId;
  }
  return axios
    .get(url, { params })
    .then((r) => r.data.component)
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

interface SearchComponentsParams extends SportArea {
  q: string; // 2-200 chars
  anchor?: string; // ISO pivot
  direction?: 'both' | 'future' | 'past';
  limit?: number;
  pageCursor?: string;
  calendarId?: string[];
  componentType?: string[];
  status?: string[];
  tags?: string[];
  priority?: string[];
  abortSignal?: AbortSignal;
}
const searchComponents = ({
  sport,
  areaId,
  q,
  anchor,
  direction,
  limit,
  pageCursor,
  calendarId,
  componentType,
  status,
  tags,
  priority,
  abortSignal,
}: SearchComponentsParams): Promise<SearchComponentsResponse> => {
  const params: Record<string, string | number> = { q };
  if (anchor) {
    params.anchor = anchor;
  }
  if (direction) {
    params.direction = direction;
  }
  if (limit) {
    params.limit = limit;
  }
  if (pageCursor) {
    params.pageCursor = pageCursor;
  }
  if (calendarId && calendarId.length) {
    params.calendarId = calendarId.join(',');
  }
  if (componentType && componentType.length) {
    params.componentType = componentType.join(',');
  }
  if (status && status.length) {
    params.status = status.join(',');
  }
  if (tags && tags.length) {
    params.tags = tags.join(',');
  }
  if (priority && priority.length) {
    params.priority = priority.join(',');
  }
  return axios
    .get(`/${sport}/areas/${areaId}/calendarcomponents/search`, {
      params,
      cancelToken: cancelTokenFor(abortSignal),
    })
    .then((r) => r.data)
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

// ── components (write) ────────────────────────────────────────────────────
interface CreateComponentParams extends SportArea {
  calendarId: string;
  component: CalendarComponentWriteBody;
  /** `x-request-id`: a retry with the same id replays the first create instead of making a second. */
  requestId?: string;
}
// PUT = create.
const createComponent = ({
  sport,
  areaId,
  calendarId,
  component,
  requestId,
}: CreateComponentParams): Promise<APICalendarComponent> =>
  axios
    .put(
      `/${sport}/areas/${areaId}/calendars/${calendarId}/components`,
      component,
      ...(requestId ? [{ headers: { 'x-request-id': requestId } }] : []),
    )
    .then((r) => r.data.component)
    .catch((e) => {
      console.log(e);
      throw e;
    });

interface UpdateComponentParams extends SportArea {
  calendarId: string;
  componentId: CalendarComponentComponentId;
  recurrenceId?: CalendarComponentRecurrenceId; // POST update uses `recurrence_id` query (snake_case)
  updateType?: 'single' | 'thisAndFuture';
  component: CalendarComponentWriteBody;
}
// POST = update.
const updateComponent = ({
  sport,
  areaId,
  calendarId,
  componentId,
  recurrenceId,
  updateType,
  component,
}: UpdateComponentParams): Promise<APICalendarComponent> => {
  const params: Record<string, string> = {};
  if (recurrenceId) {
    params.recurrence_id = recurrenceId;
  }
  const body = updateType ? { ...component, update_type: updateType } : component;
  return axios
    .post(`/${sport}/areas/${areaId}/calendars/${calendarId}/components/${componentId}`, body, {
      params,
    })
    .then((r) => r.data.component)
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

interface DeleteComponentParams extends SportArea {
  calendarId: string;
  componentId: CalendarComponentComponentId;
  recurrenceId?: CalendarComponentRecurrenceId; // DELETE uses `recurrenceId` (camelCase)
  deleteType?: 'single' | 'thisAndFuture' | 'all';
}
const deleteComponent = ({
  sport,
  areaId,
  calendarId,
  componentId,
  recurrenceId,
  deleteType,
}: DeleteComponentParams): Promise<{ success: boolean }> => {
  const params: Record<string, string> = {};
  if (recurrenceId) {
    params.recurrenceId = recurrenceId;
  }
  if (deleteType) {
    params.deleteType = deleteType;
  }
  return axios
    .delete(`/${sport}/areas/${areaId}/calendars/${calendarId}/components/${componentId}`, {
      params,
    })
    .then((r) => r.data)
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

interface UpdateProgressParams extends SportArea {
  calendarId: string;
  componentId: CalendarComponentComponentId;
  status: string;
  recurrenceId?: CalendarComponentRecurrenceId; // POST uses `recurrence_id` query (snake_case)
  updateType?: 'single' | 'thisAndFuture';
}
const updateComponentProgress = ({
  sport,
  areaId,
  calendarId,
  componentId,
  status,
  recurrenceId,
  updateType,
}: UpdateProgressParams): Promise<APICalendarComponent> => {
  const params: Record<string, string> = {};
  if (recurrenceId) {
    params.recurrence_id = recurrenceId;
  }
  const body: Record<string, string> = { status };
  if (updateType) {
    body.update_type = updateType;
  }
  return axios
    .post(
      `/${sport}/areas/${areaId}/calendars/${calendarId}/components/${componentId}/progress`,
      body,
      { params },
    )
    .then((r) => r.data.component)
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

// ── form linking ──────────────────────────────────────────────────────────
interface GetComponentFormResponsesParams extends SportArea {
  calendarId: string;
  componentId: CalendarComponentComponentId;
  recurrenceId?: CalendarComponentRecurrenceId; // GET uses `recurrenceId` (camelCase)
  formTemplateId?: string;
}
const getComponentFormResponses = ({
  sport,
  areaId,
  calendarId,
  componentId,
  recurrenceId,
  formTemplateId,
}: GetComponentFormResponsesParams): Promise<LinkedFormResponseSummary[]> => {
  const params: Record<string, string> = {};
  if (recurrenceId) {
    params.recurrenceId = recurrenceId;
  }
  if (formTemplateId) {
    params.formTemplateId = formTemplateId;
  }
  return axios
    .get(
      `/${sport}/areas/${areaId}/calendars/${calendarId}/components/${componentId}/form-responses`,
      { params },
    )
    .then((r) => (r.data && r.data.formResponses) || [])
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

interface LinkFormResponsesParams extends SportArea {
  calendarId: string;
  componentId: CalendarComponentComponentId;
  operations: LinkFormResponseOperation[];
}
const linkFormResponses = ({
  sport,
  areaId,
  calendarId,
  componentId,
  operations,
}: LinkFormResponsesParams): Promise<LinkFormResponsesResponse> =>
  axios
    .post(
      `/${sport}/areas/${areaId}/calendars/${calendarId}/components/${componentId}/link-form-responses`,
      { operations },
    )
    .then((r) => r.data)
    .catch((e) => {
      console.log(e);
      throw e;
    });

const calendarFunctions = {
  getCalendars,
  createCalendar,
  updateCalendar,
  deleteCalendar,
  getComponents,
  getComponent,
  searchComponents,
  createComponent,
  updateComponent,
  deleteComponent,
  updateComponentProgress,
  getComponentFormResponses,
  linkFormResponses,
};

export default calendarFunctions;
